import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const MOBILE_VIEWPORT_MAX_WIDTH = 640;
const MOBILE_MAX_DEVICE_PIXEL_RATIO = 1.5;
const DESKTOP_MAX_DEVICE_PIXEL_RATIO = 2;
const MOBILE_INLINE_MAX_PIXELS = 207360;
const MOBILE_FULLSCREEN_MAX_PIXELS = 368640;
const MOBILE_SHALLOW_INLINE_MAX_PIXELS = 307200;
const MOBILE_SHALLOW_FULLSCREEN_MAX_PIXELS = 614400;
const MOBILE_INLINE_MAX_SIDE = 768;
const MOBILE_FULLSCREEN_MAX_SIDE = 1280;

/**
 * Memoize serialized color parameters by their JSON representation so callers
 * can pass inline functions or range arrays without triggering downstream
 * render effects when the serialized parameters are unchanged.
 */
export function useStableColorParams(serializeColorFunc, colorFunc, deepColorRange) {
  const prevValueRef = useRef(null);
  const prevKeyRef = useRef(null);
  return useMemo(() => {
    const serialized = serializeColorFunc(colorFunc);
    const next = deepColorRange === undefined
      ? serialized
      : {
        ...serialized,
        valueRange: deepColorRange,
        ...(serialized.palette === 'fire' ? { guardedWindow: true } : {}),
      };
    try {
      const key = JSON.stringify(next);
      if (prevValueRef.current && prevKeyRef.current === key) {
        return prevValueRef.current;
      }
      prevKeyRef.current = key;
      prevValueRef.current = next;
    } catch {
      prevKeyRef.current = null;
      prevValueRef.current = next;
    }
    return next;
  }, [serializeColorFunc, colorFunc, deepColorRange]);
}

/**
 * Clamp a client pointer/touch coordinate to unit [0, 1] canvas coordinates.
 */
export function clampUnitAnchor(clientX, clientY, originLeft, originTop, width, height) {
  const rx = width > 0 ? (clientX - (originLeft || 0)) / width : 0.5;
  const ry = height > 0 ? (clientY - (originTop || 0)) / height : 0.5;
  return [
    Number.isFinite(rx) ? Math.max(0, Math.min(1, rx)) : 0.5,
    Number.isFinite(ry) ? Math.max(0, Math.min(1, ry)) : 0.5,
  ];
}

/**
 * Compute backing pixel dimensions for a shallow fractal canvas.
 */
export function computeShallowBackingSize({
  width,
  height,
  cssWidth,
  cssHeight,
  fullscreen,
  mobile = isMobileViewport(),
}) {
  const ratio = effectiveDevicePixelRatio(mobile);
  const scale = fullscreen ? 1 : Math.min(1, (cssWidth * ratio) / width);
  const rawWidth = fullscreen ? cssWidth * ratio : width * scale;
  const rawHeight = fullscreen ? cssHeight * ratio : height * scale;
  const maxSide = mobile
    ? (fullscreen ? MOBILE_FULLSCREEN_MAX_SIDE : MOBILE_INLINE_MAX_SIDE)
    : 2048;
  const maxPixels = mobile
    ? (fullscreen ? MOBILE_SHALLOW_FULLSCREEN_MAX_PIXELS : MOBILE_SHALLOW_INLINE_MAX_PIXELS)
    : Infinity;
  const reduction = Math.min(
    1,
    maxSide / Math.max(rawWidth, rawHeight, 1),
    Math.sqrt(maxPixels / Math.max(1, rawWidth * rawHeight)),
  );
  return {
    width: Math.max(1, Math.round(rawWidth * reduction)),
    height: Math.max(1, Math.round(rawHeight * reduction)),
  };
}

/**
 * Compute backing pixel dimensions for a deep-zoom fractal canvas.
 */
export function computeDeepBackingSize({
  cssWidth,
  cssHeight,
  fullscreen,
  pixelLimit,
  maxSide = 2048,
  mobile = isMobileViewport(),
}) {
  const pixelRatio = effectiveDevicePixelRatio(mobile);
  const rawWidth = Math.max(1, cssWidth * pixelRatio);
  const rawHeight = Math.max(1, cssHeight * pixelRatio);
  const effectivePixelLimit = mobile
    ? Math.min(pixelLimit, fullscreen ? MOBILE_FULLSCREEN_MAX_PIXELS : MOBILE_INLINE_MAX_PIXELS)
    : pixelLimit;
  const effectiveMaxSide = mobile
    ? (fullscreen ? MOBILE_FULLSCREEN_MAX_SIDE : MOBILE_INLINE_MAX_SIDE)
    : maxSide;
  const reduction = Math.min(
    1,
    Math.sqrt(effectivePixelLimit / (rawWidth * rawHeight)),
    effectiveMaxSide / rawWidth,
    effectiveMaxSide / rawHeight,
  );
  return {
    width: Math.max(1, Math.floor(rawWidth * reduction)),
    height: Math.max(1, Math.floor(rawHeight * reduction)),
  };
}

/**
 * Return true when the current browser viewport matches the <=640px mobile layout.
 */
export function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  const vw = window.innerWidth;
  if (Number.isFinite(vw) && vw > 0 && vw <= MOBILE_VIEWPORT_MAX_WIDTH) return true;
  const vh = window.innerHeight;
  return Number.isFinite(vw) && Number.isFinite(vh)
    && vw > 0 && vh > 0
    && vh <= 500 && vw <= 1024;
}

/**
 * Cap effective devicePixelRatio so 3x+ phone screens do not allocate 9x+ pixel buffers.
 */
function effectiveDevicePixelRatio(mobile = isMobileViewport()) {
  const raw = typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio) && window.devicePixelRatio > 0
    ? window.devicePixelRatio
    : 1;
  return Math.min(raw, mobile ? MOBILE_MAX_DEVICE_PIXEL_RATIO : DESKTOP_MAX_DEVICE_PIXEL_RATIO);
}

/**
 * Compute complex-plane span multipliers when transitioning a canvas between
 * aspect ratios (such as inline mini mode and full-viewport fullscreen mode)
 * while keeping the camera center fixed and pixels square.
 */
export function aspectSpanMultipliers(fromAspect, toAspect, baseAspect) {
  if (!Number.isFinite(fromAspect) || !Number.isFinite(toAspect) || !Number.isFinite(baseAspect)
    || fromAspect <= 0 || toAspect <= 0 || baseAspect <= 0
    || Math.abs(fromAspect - toAspect) <= 1e-6 * Math.max(fromAspect, toAspect)) {
    return { scaleX: 1, scaleY: 1 };
  }
  const fromX = fromAspect >= baseAspect ? fromAspect / baseAspect : 1;
  const fromY = fromAspect < baseAspect ? baseAspect / fromAspect : 1;
  const toX = toAspect >= baseAspect ? toAspect / baseAspect : 1;
  const toY = toAspect < baseAspect ? baseAspect / toAspect : 1;
  return {
    scaleX: toX / fromX,
    scaleY: toY / fromY,
  };
}

/**
 * Scale [min, max] coordinate ranges around their current center to match a
 * new canvas aspect ratio without distorting the fractal.
 */
export function adjustRangesForAspect(xRange, yRange, fromAspect, toAspect, baseAspect) {
  const { scaleX, scaleY } = aspectSpanMultipliers(fromAspect, toAspect, baseAspect);
  const changeX = Math.abs(scaleX - 1) > 1e-12;
  const changeY = Math.abs(scaleY - 1) > 1e-12;
  if (!changeX && !changeY) {
    return { xRange, yRange, changed: false };
  }
  const centerX = (xRange[0] + xRange[1]) * 0.5;
  const centerY = (yRange[0] + yRange[1]) * 0.5;
  const halfSpanX = (xRange[1] - xRange[0]) * 0.5 * scaleX;
  const halfSpanY = (yRange[1] - yRange[0]) * 0.5 * scaleY;
  const nextXRange = changeX ? [centerX - halfSpanX, centerX + halfSpanX] : [xRange[0], xRange[1]];
  const nextYRange = changeY ? [centerY - halfSpanY, centerY + halfSpanY] : [yRange[0], yRange[1]];
  if (!Number.isFinite(nextXRange[0]) || !Number.isFinite(nextXRange[1])
    || !Number.isFinite(nextYRange[0]) || !Number.isFinite(nextYRange[1])
    || !Number.isFinite(nextXRange[1] - nextXRange[0])
    || !Number.isFinite(nextYRange[1] - nextYRange[0])
    || !(nextXRange[1] > nextXRange[0])
    || !(nextYRange[1] > nextYRange[0])) {
    return { xRange, yRange, changed: false };
  }
  return {
    xRange: nextXRange,
    yRange: nextYRange,
    changed: true,
  };
}

/**
 * Format a numeric coordinate to `sigFigs` significant figures for compact UI display.
 */
function formatCoordinateSigFigs(value, sigFigs = 3) {
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return (num === 0 ? 0 : num).toPrecision(sigFigs);
}

export function formatCenter(centerX, centerY, sigFigs = 3) {
  return `(${formatCoordinateSigFigs(centerX, sigFigs)}, ${formatCoordinateSigFigs(centerY, sigFigs)})`;
}

function isZenModeToggleShortcut(event) {
  if (!event || (!event.metaKey && !event.ctrlKey) || event.altKey || event.shiftKey) {
    return false;
  }
  return event.key === '\\' || event.code === 'Backslash';
}

/**
 * Return true when a canvas's immediate container is in full-viewport fullscreen mode.
 */
export function isCanvasFullscreen(canvas) {
  return canvas?.parentElement?.dataset?.fullscreen === 'true';
}

let sharedResizeObserverCtor = null;
let sharedResizeObserver = null;
const resizeCallbacks = new Map();
let sharedResizeListening = false;
let sharedPixelRatioQuery = null;

function notifyAllResizeCallbacks() {
  for (const cb of [...resizeCallbacks.values()]) cb();
}

function watchSharedPixelRatio() {
  if (typeof window === 'undefined') return;
  sharedPixelRatioQuery?.removeEventListener?.('change', onSharedPixelRatioChange);
  sharedPixelRatioQuery = window.matchMedia?.(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
  sharedPixelRatioQuery?.addEventListener?.('change', onSharedPixelRatioChange);
}

function onSharedPixelRatioChange() {
  notifyAllResizeCallbacks();
  watchSharedPixelRatio();
}

/**
 * Observe a canvas element for CSS box size changes, window resize events, and
 * devicePixelRatio media query transitions using a single shared observer and
 * window listener across all mounted canvases on the page.
 */
export function observeCanvasResize(canvasEl, onResize) {
  if (!canvasEl) return () => {};
  if (typeof ResizeObserver !== 'undefined') {
    if (sharedResizeObserverCtor !== ResizeObserver || !sharedResizeObserver) {
      sharedResizeObserver?.disconnect?.();
      sharedResizeObserverCtor = ResizeObserver;
      resizeCallbacks.clear();
      sharedResizeObserver = new ResizeObserver(entries => {
        if (!Array.isArray(entries) || entries.length === 0) {
          notifyAllResizeCallbacks();
          return;
        }
        for (const entry of entries) {
          const cb = entry?.target && resizeCallbacks.get(entry.target);
          if (cb) cb();
          else if (!entry?.target) notifyAllResizeCallbacks();
        }
      });
    }
    sharedResizeObserver.observe(canvasEl);
  }
  resizeCallbacks.set(canvasEl, onResize);
  if (!sharedResizeListening && typeof window !== 'undefined') {
    window.addEventListener('resize', notifyAllResizeCallbacks);
    watchSharedPixelRatio();
    sharedResizeListening = true;
  }
  return () => {
    if (!resizeCallbacks.delete(canvasEl)) return;
    if (resizeCallbacks.size === 0) {
      sharedResizeObserver?.disconnect?.();
      sharedResizeObserver = null;
      if (sharedResizeListening && typeof window !== 'undefined') {
        window.removeEventListener('resize', notifyAllResizeCallbacks);
        sharedPixelRatioQuery?.removeEventListener?.('change', onSharedPixelRatioChange);
        sharedPixelRatioQuery = null;
        sharedResizeListening = false;
      }
    } else {
      sharedResizeObserver?.unobserve?.(canvasEl);
    }
  };
}

/**
 * If page layout reflowed while a fractal was in fixed fullscreen mode (for
 * example after a mobile portrait/landscape rotation), scroll its inline
 * container back into the viewport when exiting fullscreen so it stays in view.
 */
export function keepContainerInViewOnFullscreenExit(containerEl) {
  if (!containerEl || globalThis.IntersectionObserver?.mock) return;
  const rect = containerEl.getBoundingClientRect?.();
  if (rect && rect.height > 0 && typeof window !== 'undefined' && window.innerHeight > 0
    && (rect.bottom <= 0 || rect.top >= window.innerHeight)) {
    try {
      containerEl.scrollIntoView?.({ block: 'center', behavior: 'instant' });
    } catch {
      containerEl.scrollIntoView?.();
    }
  }
}

function syncBodyClassOnUnmount(className, selector, containerEl) {
  const otherActive = Array.from(
    document.querySelectorAll?.(selector) || []
  ).some(el => el !== containerEl && el.isConnected);
  if (!otherActive) {
    document.body?.classList.remove(className);
  }
}

/**
 * Manage fullscreen state for an interactive fractal canvas container,
 * keeping the view in full-viewport mode across renderer transitions,
 * toggling zen mode (hiding UI elements) on Cmd/Ctrl+\, and exiting when
 * Escape is pressed.
 */
export function useFullscreenControl({
  isFullscreen: controlledFullscreen,
  onFullscreenChange,
  isZenMode: controlledZenMode,
  onZenModeChange,
} = {}) {
  const containerRef = useRef(null);
  const [uncontrolledFullscreen, setUncontrolledFullscreen] = useState(false);
  const [uncontrolledZenMode, setUncontrolledZenMode] = useState(false);
  const isFullscreen = Boolean(controlledFullscreen ?? uncontrolledFullscreen);
  const isFullscreenRef = useRef(isFullscreen);
  isFullscreenRef.current = isFullscreen;
  const isZenMode = Boolean(isFullscreen && (controlledZenMode ?? uncontrolledZenMode));
  const isZenModeRef = useRef(isZenMode);
  isZenModeRef.current = isZenMode;

  const setZenMode = useCallback(next => {
    const value = isFullscreenRef.current
      ? (typeof next === 'function'
        ? Boolean(next(isZenModeRef.current))
        : Boolean(next))
      : false;
    isZenModeRef.current = value;
    if (controlledZenMode === undefined) {
      setUncontrolledZenMode(value);
    }
    onZenModeChange?.(value);
  }, [controlledZenMode, onZenModeChange]);

  const setFullscreen = useCallback(next => {
    const value = typeof next === 'function'
      ? Boolean(next(isFullscreenRef.current))
      : Boolean(next);
    isFullscreenRef.current = value;
    if (controlledFullscreen === undefined) {
      setUncontrolledFullscreen(value);
    }
    onFullscreenChange?.(value);
    if (!value && isZenModeRef.current) {
      isZenModeRef.current = false;
      if (controlledZenMode === undefined) {
        setUncontrolledZenMode(false);
      }
      onZenModeChange?.(false);
    }
  }, [controlledFullscreen, onFullscreenChange, controlledZenMode, onZenModeChange]);

  const toggleFullscreen = useCallback(() => {
    setFullscreen(prev => !prev);
  }, [setFullscreen]);

  useEffect(() => {
    if (!isFullscreen) {
      if (uncontrolledZenMode) {
        setUncontrolledZenMode(false);
      }
      return undefined;
    }

    const handleKeyDown = event => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        setFullscreen(false);
        return;
      }
      if (isZenModeToggleShortcut(event)) {
        const target = event.target;
        if (target?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName || '')) {
          return;
        }
        event.preventDefault();
        setZenMode(prev => !prev);
      }
    };

    const containerEl = containerRef.current;
    document.body?.classList.add('fullscreen-active');
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      syncBodyClassOnUnmount('fullscreen-active', '.image-container.fullscreen', containerEl);
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isFullscreen, uncontrolledZenMode, setFullscreen, setZenMode]);

  useEffect(() => {
    if (!isZenMode) return undefined;
    const containerEl = containerRef.current;
    document.body?.classList.add('zen-mode-active');
    return () => {
      syncBodyClassOnUnmount('zen-mode-active', '.image-container.zen-mode', containerEl);
    };
  }, [isZenMode]);

  return {
    containerRef,
    isFullscreen,
    isFullscreenRef,
    toggleFullscreen,
    isZenMode,
  };
}

export function FullscreenButton({ isFullscreen, onToggle }) {
  const label = isFullscreen ? 'Exit fullscreen' : 'Fullscreen';
  return (
    <button
      type="button"
      className="fullscreen-button"
      aria-label={label}
      aria-pressed={isFullscreen}
      title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}
      onClick={event => {
        event.stopPropagation();
        onToggle();
      }}
    >
      <svg
        className="fullscreen-icon"
        viewBox="0 0 24 24"
        width="20"
        height="20"
        aria-hidden="true"
        focusable="false"
      >
        {isFullscreen ? (
          <path
            fill="currentColor"
            d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
          />
        ) : (
          <path
            fill="currentColor"
            d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
          />
        )}
      </svg>
    </button>
  );
}
